/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import { FORM_JOB_TYPES, RECURRENCE_FREQUENCIES, DAYS_OF_WEEK } from "@constants";
import utils from "@utils";

import { DatetimeInput, TimeInput, Tooltip, ReactSelect } from "../../../../components";

export const isDateValid = value => {
	const formattedStartDate = utils.isIOSDevice ? value.replace(/-/gi, "/") : value;
	return !Number.isNaN(new Date(`${formattedStartDate} 00:00`).getTime());
};

export const isDateInvalid = date => {
	if (!date) return true;
	if ((utils.isIE || utils.isDesktopSafari) && date.replace(/_/g, "").length !== 10) return true;
	return false;
};

export const isTimeInvalid = (jobEventTypeId, time) => {
	if (jobEventTypeId !== 6 && !time) return true;
	if (
		(utils.isIE || utils.isDesktopSafari) &&
		jobEventTypeId !== 6 &&
		time.replace(/_/g, "").length < 5
	)
		return true;
	return false;
};

/**
 * Converts a given time into a decimal formatted value for calculating job duration
 * @param {String} time
 * @returns {Number} a decimal representation of how much time has passed from 12:00 AM to the time provided
 */
export const timeToHours = (user, time) => {
	let string = time.split(":");
	string = string.map(num => parseInt(num.replace(/\D/gi), 10));
	// Safari/IE time is 1- 12 so we must complensate and add to the hours
	if (!user.UserPreferences) {
		if ((utils.isDesktopSafari || utils.isIE) && time.includes("PM")) {
			switch (string[0]) {
				case 12:
					break;
				default:
					string[0] += 12;
					break;
			}
		} else if ((utils.isDesktopSafari || utils.isIE) && string[0] === 12 && time.includes("AM"))
			string[0] = 0;
	}
	const [hrs, min] = string;
	return hrs + Math.round((min / 60 + Number.EPSILON) * 100) / 100;
};

const DateSelector = ({ form, user, updateForm, validateForm, jobEditingProperties }) => {
	const { lang } = global;
	const minStartDate = utils.formatDate(undefined, undefined, undefined, "noslash");
	const recurrenceFrequencyOptions = Object.values(RECURRENCE_FREQUENCIES);
	const startDateIsValid = () => isDateValid(form._startDate);
	// Calculate the job duration
	let jobDuration = timeToHours(user, form?._endTime) - timeToHours(user, form?._startTime);
	// If the number is negative this means the End Time is 12 AM which equates to 0 so we must add 24 to compensate
	if (jobDuration < 0) {
		jobDuration += 24;
	}

	const [customDateInput, setCustomDateInput] = useState("");
	const [jobStartDOW, setJobStartDOW] = useState("");

	const minCustomDate = () => {
		if (startDateIsValid()) {
			const formattedStart = utils.isIOSDevice
				? form._startDate.replace(/-/gi, "/")
				: form._startDate;
			const startDate = new Date(`${formattedStart} 00:00`).getTime();
			return utils.formatDate(
				new Date(startDate + 86400000),
				undefined,
				undefined,
				"noslash",
			);
		}
		return utils.formatDate(
			new Date(new Date().getTime() + 86400000),
			undefined,
			undefined,
			"noslash",
		);
	};

	/**
	 * Changes the minimum number of hours required to be worked for a flex job
	 * @param {Event} event The HTML event handled by this method
	 */
	const onFlexHoursInputChange = event => {
		const {
			target: { value },
		} = event;
		if (!value.match(/^\d*\.?\d{0,2}$/g)) return;
		updateForm("generalInfo", { _flexJobMinHours: value }, false, validateForm);
	};

	const handleChange =
		(property, type = "text") =>
		event => {
			let value = "";
			if (type === "select") value = event?.value || "";
			else if (type === "text")
				({
					target: { value },
				} = event);
			else if (type === "checkbox")
				({
					target: { checked: value },
				} = event);
			else value = event;
			const {
				JobEventRecurrenceRule: { ...JobEventRecurrenceRule },
				_startDate,
			} = form;
			const updates = { [property]: value, JobEventRecurrenceRule };
			const newEstimate = [
				"_startTime",
				"_endTime",
				"_isRecurring",
				"_flexJob",
				"_flexJobMinHours",
			].includes(property);
			if (property === "_isRecurring" && value === false) {
				// For date inputs on iOS, all '/' characters need to be replaced with '-'
				let StartsOn = utils.isIOSDevice ? _startDate.replace(/\//gi, "-") : _startDate;

				// If the displayed recurrence start date is not mm/dd/yyyy, format it so that it is
				if (utils.isIOSDevice) {
					// For display purposes, we always want to show this date with '/' characters, even on iOS
					StartsOn = StartsOn.replace(/-/gi, "/");
				}
				if (!utils.isDesktopSafari && !utils.isIE) {
					StartsOn = utils.formatDate(new Date(`${StartsOn} 00:00`), "en-US");
				}
				updates.JobEventRecurrenceRule = {
					StartsOn,
					RepeatType: 1,
					RepeatsOn: [],
					EndType: "NEVER",
					EndsAfterOccurrence: "",
					EndsOn: "",
					WeekFrequency: "1",
					CustomDates: [],
				};
			}
			if (property === "_flexJob" && value === false) updates._flexJobMinHours = "";

			updateForm("generalInfo", updates, newEstimate, validateForm);
		};

	/**
	 * Updates the HOC with changes to the job start date input field. Additionally, this method performs proper formatting for
	 * API requests and JavaScript `Date` object handling as well
	 * @param {Event} event the HTML event handled by this method
	 */
	const onJobDateChange = ({ target: { value } }) => {
		// For date inputs on iOS, all '/' characters need to be replaced with '-'
		const formattedValue = utils.isIOSDevice ? value.replace(/\//gi, "-") : value;

		// If the displayed recurrence start date is not mm/dd/yyyy, format it so that it is
		let recurrenceStartsOn = formattedValue;
		if (utils.isIOSDevice) {
			// For display purposes, we always want to show this date with '/' characters, even on iOS
			recurrenceStartsOn = recurrenceStartsOn.replace(/-/gi, "/");
		}
		if (!utils.isDesktopSafari && !utils.isIE) {
			recurrenceStartsOn = utils.formatDate(new Date(`${recurrenceStartsOn} 00:00`), "en-US");
		}

		const updates = {
			_startDate: formattedValue,
			JobEventRecurrenceRule: {
				...form.JobEventRecurrenceRule,
				StartsOn: recurrenceStartsOn,
			},
		};

		const {
			JobEventRecurrenceRule: { RepeatsOn, RepeatType },
		} = form;
		const dayOfWeek = new Date(`${formattedValue} 00:00`).getDay();
		const startDay = DAYS_OF_WEEK.find(item => dayOfWeek === item.value) || {};
		const weekDays = DAYS_OF_WEEK.filter(item => item.value >= 1 && item.value <= 5).map(
			day => day.shortName,
		);
		const weekEnds = DAYS_OF_WEEK.filter(item => item.value === 0 || item.value === 6).map(
			day => day.shortName,
		);
		if (RepeatType !== RECURRENCE_FREQUENCIES.DAILY.value) {
			if (isDateValid(formattedValue)) {
				setJobStartDOW(startDay.shortName || "");
				if (RepeatType === 3) {
					updates.JobEventRecurrenceRule.RepeatsOn = [...weekEnds];
					if (RepeatsOn.indexOf(startDay.shortName) === -1 && startDay.shortName)
						weekEnds.push(startDay.shortName);
					updates.JobEventRecurrenceRule.RepeatsOn = [...weekEnds];
				}
				if (RepeatType === 2) {
					updates.JobEventRecurrenceRule.RepeatsOn = [...weekDays];
					if (RepeatsOn.indexOf(startDay.shortName) === -1 && startDay.shortName)
						weekDays.push(startDay.shortName);
					updates.JobEventRecurrenceRule.RepeatsOn = [...weekDays];
				}
				if (RepeatType === 4) {
					updates.JobEventRecurrenceRule.RepeatsOn = [...RepeatsOn];
					if (RepeatsOn.indexOf(startDay.shortName) === -1 && startDay.shortName) {
						updates.JobEventRecurrenceRule.RepeatsOn = [startDay.shortName];
					}
				}
			}
		} else if (RepeatType === RECURRENCE_FREQUENCIES.DAILY.value) {
			if (jobStartDOW !== "") setJobStartDOW("");
			updates.JobEventRecurrenceRule.RepeatsOn = [];
		}
		updateForm("generalInfo", updates, true, validateForm);
	};

	/**
	 * Handles changes to the recurrence type and update the HOC with latest values
	 * @param {Object} event An object with `value` and `label` properties returned by a `ReactSelect` element when the selected value has been changed
	 */
	const onRecurrenceRepeatTypeChange = event => {
		const updates = {
			JobEventRecurrenceRule: {
				...form.JobEventRecurrenceRule,
				RepeatType: event.value,
			},
		};
		const { _startDate } = form;
		const dayOfWeek = new Date(`${_startDate} 00:00`).getDay();
		if (event.value !== RECURRENCE_FREQUENCIES.CUSTOM.value) {
			updates.JobEventRecurrenceRule.CustomDates = [];
			setCustomDateInput("");
		}
		if (
			event.value !== RECURRENCE_FREQUENCIES.DAILY.value &&
			event.value !== RECURRENCE_FREQUENCIES.CUSTOM.value
		) {
			const weekDays = DAYS_OF_WEEK.filter(item => item.value >= 1 && item.value <= 5).map(
				day => day.shortName,
			);
			const weekEnds = DAYS_OF_WEEK.filter(item => item.value === 0 || item.value === 6).map(
				day => day.shortName,
			);
			const startDay = DAYS_OF_WEEK.find(item => dayOfWeek === item.value) || {};

			if (startDateIsValid()) {
				if (weekEnds.indexOf(startDay.shortName) === -1 && startDay.shortName)
					weekEnds.push(startDay.shortName);
				setJobStartDOW(startDay.shortName || "");
				if (startDay.shortName && event.value === 4) {
					updates.JobEventRecurrenceRule.RepeatsOn = [startDay.shortName];
				}
			} else if (!startDateIsValid() && event.value === 4)
				updates.JobEventRecurrenceRule.RepeatsOn = [];
			if (event.value === 2) updates.JobEventRecurrenceRule.RepeatsOn = [...weekDays];
			if (event.value === 3) updates.JobEventRecurrenceRule.RepeatsOn = [...weekEnds];
		} else if (event.value === RECURRENCE_FREQUENCIES.DAILY.value) {
			if (jobStartDOW !== "") setJobStartDOW("");
			updates.JobEventRecurrenceRule.RepeatsOn = [];
			updates.JobEventRecurrenceRule.WeekFrequency = "1";
		}

		updateForm("generalInfo", updates);
	};

	/**
	 * Updates the HOC with the latest value for which days the job should repeat on
	 * @param {String} value The short name of the day to add to/remove from the `RepeatsOn` array
	 */
	const onRecurrenceRepeatsOnChange = value => {
		const {
			JobEventRecurrenceRule: { RepeatsOn, RepeatType },
		} = form;
		const updates = {
			JobEventRecurrenceRule: {
				...form.JobEventRecurrenceRule,
				RepeatsOn,
			},
		};

		if (RepeatType !== 4) updates.JobEventRecurrenceRule.RepeatType = 4;

		if (RepeatsOn.indexOf(value) === -1) {
			updates.JobEventRecurrenceRule.RepeatsOn = [...RepeatsOn, value];
		} else {
			updates.JobEventRecurrenceRule.RepeatsOn = RepeatsOn.filter(item => item !== value);
		}
		updateForm("generalInfo", updates);
	};

	/**
	 * Updates the HOC with the latest value for how the recurrence end is determined
	 * @param {'NEVER'|'OCCURRENCE'|'DATE'} value
	 */
	const onRecurrenceEndTypeChange = value => {
		const updates = {
			JobEventRecurrenceRule: {
				...form.JobEventRecurrenceRule,
				EndType: value,
			},
		};

		switch (value) {
			case "NEVER":
				updates.JobEventRecurrenceRule.EndsAfterOccurrence = "";
				updates.JobEventRecurrenceRule.EndsOn = "";
				break;
			case "OCCURRENCE":
				updates.JobEventRecurrenceRule.EndsOn = "";
				break;
			case "DATE":
				updates.JobEventRecurrenceRule.EndsAfterOccurrence = "";
				break;
			default:
				break;
		}
		updateForm("generalInfo", updates);
	};

	/**
	 * Changes how many weeks should pass before repeating a job series
	 * @param {Event} event The HTML event handled by this method
	 */
	const onWeekFrequencyInputChange = event => {
		const {
			target: { value },
		} = event;
		let newValue = "";
		newValue = value.replace(/\D/g, "");
		if (parseInt(newValue, 10) > 52) {
			newValue = "52";
		}
		if (parseInt(newValue, 10) < 1) {
			newValue = "1";
		}
		const updates = {
			JobEventRecurrenceRule: {
				...form.JobEventRecurrenceRule,
				WeekFrequency: newValue,
			},
		};
		updateForm("generalInfo", updates, false, validateForm);
	};

	/**
	 * Updates the HOC with the latest value for the given property
	 * @param {String} property The recurrence property to update value for
	 */
	const onRecurrenceEndInputChange =
		property =>
		({ target: { value } }) => {
			// updateValue added to make sure user cant type non integer value in the input field.
			let updateValue = value;
			if (property === "EndsAfterOccurrence") {
				updateValue = value.replace(/\D/gi, "");
			}
			const updates = { JobEventRecurrenceRule: { ...form.JobEventRecurrenceRule } };
			updates.JobEventRecurrenceRule[property] = updateValue;
			updateForm("generalInfo", updates, true, validateForm);
		};

	const handleAddCustomDate = () => {
		const {
			JobEventRecurrenceRule: { CustomDates },
		} = form;
		const input = document.getElementById("customDate");
		if (!customDateInput || CustomDates.includes(customDateInput)) {
			input.classList.add("invalid-customDate");
			return;
		}
		// Make sure the chosen date is after the job start date
		let startDate = new Date(`${form._startDate}T00:00:00`);
		let customDate = new Date(`${customDateInput}T00:00:00`);
		if (utils.isDesktopSafari || utils.isIE) {
			startDate = new Date(`${form._startDate} 00:00`);
			customDate = new Date(`${customDateInput} 00:00`);
		}
		if (startDate.getTime() > customDate.getTime()) {
			utils.notify({
				type: "error",
				message: "Selected date must be after the job start date",
				timeOut: 1500,
			});
			return;
		}
		input.classList.remove("invalid-customDate");
		const updates = { JobEventRecurrenceRule: { ...form.JobEventRecurrenceRule } };
		updates.JobEventRecurrenceRule.CustomDates = [...CustomDates, customDateInput];
		setCustomDateInput("");
		input.focus();

		updateForm("generalInfo", updates, true, validateForm);
	};

	const deleteCustomDate = date => {
		const {
			JobEventRecurrenceRule: { CustomDates },
		} = form;
		const updates = { JobEventRecurrenceRule: { ...form.JobEventRecurrenceRule } };
		updates.JobEventRecurrenceRule.CustomDates = CustomDates.filter(i => i !== date);
		updateForm("generalInfo", updates, true, validateForm);
	};

	// Detect changes in recurrence repeat type (daily, weekly, weekends, weekdays)
	useEffect(() => {
		// Attempt to disable the correct day of week checkbox if daily recurrence is selected
		const {
			_startDate,
			JobEventRecurrenceRule: { RepeatType, RepeatsOn },
		} = form;
		const JobEventRecurrenceRule = { ...form.JobEventRecurrenceRule };
		if (RepeatType !== RECURRENCE_FREQUENCIES.DAILY.value) {
			if (isDateValid(_startDate)) {
				const dayOfWeek = new Date(`${_startDate} 00:00`).getDay();
				const startDay = DAYS_OF_WEEK.find(item => dayOfWeek === item.value) || {};
				setJobStartDOW(startDay.shortName || "");
				if (startDay.shortName && RepeatsOn.indexOf(startDay.shortName) === -1) {
					JobEventRecurrenceRule.RepeatsOn = [...RepeatsOn, startDay.shortName];
				}
			}
		} else if (RepeatType === RECURRENCE_FREQUENCIES.DAILY.value) {
			if (jobStartDOW !== "") setJobStartDOW("");
			JobEventRecurrenceRule.RepeatsOn = [];
		}
		if (JobEventRecurrenceRule.RepeatsOn !== form.JobEventRecurrenceRule.RepeatsOn) {
			updateForm("generalInfo", { JobEventRecurrenceRule });
		}
	}, [form.JobEventRecurrenceRule.RepeatType]);

	return (
		<div className="group-box">
			<div className="row">
				<div
					className={classNames("col-xs-12 col-sm-4 form-group", {
						"invalid-field": isDateInvalid(form._startDate),
					})}
				>
					<label>
						{lang.t("Start Date")}
						<span className="required-mark" />
					</label>
					<DatetimeInput
						id="jobDate"
						name="jobDate"
						minStartDate={minStartDate}
						startDate={form._startDate}
						onJobDateChange={onJobDateChange}
						data-test-id="jobs__job-date"
					/>
				</div>
				<If condition={form.JobEventTypeId !== FORM_JOB_TYPES.EVENT.value}>
					<div
						className={classNames("col-xs-6 col-sm-4 form-group", {
							"invalid-field": isTimeInvalid(form.JobEventTypeId, form._startTime),
						})}
					>
						<label>
							{lang.t("Start")}
							<span className="required-mark" />
						</label>
						<TimeInput
							name="jobStartTime"
							id="jobStartTime"
							className={classNames("form-control")}
							time={form._startTime}
							handleChange={handleChange("_startTime")}
							user={user}
							data-test-id="jobs__job-start-time"
						/>
					</div>
					<div
						className={classNames("col-xs-6 col-sm-4 form-group", {
							"invalid-field": isTimeInvalid(form.JobEventTypeId, form._endTime),
						})}
					>
						<label>
							{lang.t("End")}
							<span className="required-mark" />
						</label>
						<TimeInput
							name="jobEndTime"
							id="jobEndTime"
							time={form._endTime}
							className={classNames("form-control")}
							handleChange={handleChange("_endTime")}
							user={user}
							data-test-id="jobs__job-end-time"
						/>
					</div>
				</If>
			</div>
			<If condition={form.JobEventTypeId !== FORM_JOB_TYPES.EVENT.value}>
				<div className="row">
					<div className="col-xs-12">
						{lang.t("Is this a flexible shift_")}
						<Tooltip
							content={lang.t(
								"Flex jobs allow the officer to work any time within the specified hours, and do not conflict with scheduled jobs",
							)}
						>
							<i className="rkf rkf-question-solid job-form-tooltip tooltip-icon" />
						</Tooltip>
					</div>
					<div className="col-xs-4 col-sm-2 radio">
						<label htmlFor="flexibleNo">
							<input
								type="radio"
								name="FlexJob"
								id="flexibleNo"
								checked={form._flexJob === false}
								onChange={() => handleChange("_flexJob", "value")(false)}
								data-test-id="jobs__flex-job-no"
							/>
							{lang.t("No")}
						</label>
					</div>
					<div className="col-xs-4 col-sm-2 radio">
						<label htmlFor="flexibleYes">
							<input
								type="radio"
								name="FlexJob"
								id="flexibleYes"
								checked={form._flexJob === true}
								onChange={() => handleChange("_flexJob", "value")(true)}
							/>
							{lang.t("Yes")}
						</label>
					</div>
				</div>
				<If condition={form._flexJob}>
					<div className="row form-group">
						<div
							className={classNames("weekly-repeat", {
								"invalid-field":
									form._flexJob &&
									(form._flexJobMinHours === "" ||
										parseFloat(form._flexJobMinHours) === 0 ||
										parseFloat(form._flexJobMinHours) > jobDuration ||
										parseFloat(form._flexJobMinHours) >= 24),
							})}
						>
							<span>{lang.t("Minimum")}</span>
							<input
								type="tel"
								name="FlexJobMinHours"
								id="FlexJobMinHours"
								value={form._flexJobMinHours}
								className="form-control"
								onChange={e => onFlexHoursInputChange(e)}
								onKeyPress={e => {
									if (e.key === "Enter") onFlexHoursInputChange(e);
								}}
								data-test-id="jobs__flex-job-min-hours"
							/>
							<span>{lang.t("Hours Required")}</span>
						</div>
					</div>
				</If>
			</If>
			<If condition={form.JobEventTypeId !== FORM_JOB_TYPES.EVENT.value}>
				<div className="row">
					<div className="col-xs-12">
						{lang.t("Is this a recurring job_")}
						<Tooltip
							content={lang.t(
								"Let RollKall do the heavy lifting of scheduling out a job that repeats daily or on specific days of the week",
							)}
						>
							<i className="rkf rkf-question-solid job-form-tooltip tooltip-icon" />
						</Tooltip>
					</div>
					<div className="col-xs-4 col-sm-2 radio">
						<label htmlFor="recurringNo">
							<input
								type="radio"
								name="_isRecurring"
								id="recurringNo"
								checked={!form._isRecurring}
								onChange={() => handleChange("_isRecurring", "value")(false)}
								disabled={!!jobEditingProperties.JobEventRecurrenceRuleId}
								data-test-id="jobs__recurring-no"
							/>
							{lang.t("No")}
						</label>
					</div>
					<div className="col-xs-4 col-sm-2 radio">
						<label htmlFor="recurringYes">
							<input
								type="radio"
								name="_isRecurring"
								id="recurringYes"
								checked={form._isRecurring}
								onChange={() => handleChange("_isRecurring", "value")(true)}
								disabled={!!jobEditingProperties.JobEventRecurrenceRuleId}
								data-test-id="jobs__recurring-yes"
							/>
							{lang.t("Yes")}
						</label>
					</div>
				</div>
				<If condition={form._isRecurring}>
					<div className="row">
						<div className="col-sm-6 form-group">
							<label>{lang.t("Repeats")}</label>
							<ReactSelect
								id="recurrence-repeat-type"
								name="jobEventRecurrenceRepeatType"
								value={form.JobEventRecurrenceRule.RepeatType}
								onChange={onRecurrenceRepeatTypeChange}
								onMenuClose={validateForm}
								options={recurrenceFrequencyOptions}
							/>
						</div>
						<Choose>
							<When
								condition={
									form.JobEventRecurrenceRule.RepeatType ===
									RECURRENCE_FREQUENCIES.CUSTOM.value
								}
							>
								<div className="col-xs-9 col-sm-4 form-group">
									<label htmlFor="customDate">
										{lang.t("Select")} {lang.t("Date")}
									</label>
									<DatetimeInput
										name="customDate"
										id="customDate"
										minStartDate={minCustomDate()}
										onJobDateChange={e => setCustomDateInput(e.target.value)}
										startDate={customDateInput}
									/>
								</div>
								<div className="col-xs-3 col-sm-2 text-center">
									<br />
									<button
										type="button"
										id="add-specific-date"
										aria-label="add specific date"
										onClick={handleAddCustomDate}
										className="btn btn-link"
									>
										+ {lang.t("Add")}
									</button>
								</div>
								<If condition={form.JobEventRecurrenceRule.CustomDates.length > 0}>
									<div className="col-xs-12">
										<strong>{lang.t("Dates")}</strong>
										<ul className="list-unstyled custom-dates">
											<For
												each="date"
												of={form.JobEventRecurrenceRule.CustomDates}
											>
												<li key={`custom-date-${date.replace(/\//g, "-")}`}>
													<button
														type="button"
														data-cd={date}
														className="btn btn-rounded"
														onClick={() => deleteCustomDate(date)}
													>
														{utils.formatDate(
															`${utils.fixIELocaleString(date)}T00:00:00`,
															"en-US",
															undefined,
															"2-digit",
														)}
														<span className="badge">&#10005;</span>
													</button>
												</li>
											</For>
										</ul>
									</div>
								</If>
							</When>
							<Otherwise>
								<div className="col-sm-4 form-group">
									<label>{lang.t("Starts")}</label>
									<div>
										{utils.formatDate(
											`${form._startDate}${utils.isIOSDevice ? "T" : " "}00:00`,
											"en-US",
											undefined,
											"2-digit",
										)}
									</div>
								</div>
							</Otherwise>
						</Choose>
					</div>
					<If
						condition={
							form.JobEventRecurrenceRule.RepeatType ===
							RECURRENCE_FREQUENCIES.WEEKLY.value
						}
					>
						<div className="row form-group">
							<div
								className={classNames("weekly-repeat", {
									"invalid-field":
										form.JobEventRecurrenceRule.RepeatType === 4 &&
										form.JobEventRecurrenceRule.WeekFrequency === "",
								})}
							>
								<span>{lang.t("Repeats Every")}</span>
								<input
									type="tel"
									name="jobEventRecurrenceWeekFrequency"
									id="jobEventRecurrenceWeekFrequency"
									value={form.JobEventRecurrenceRule.WeekFrequency}
									className={classNames("form-control", {
										"invalid-field":
											form.JobEventRecurrenceRule.RepeatType === 4 &&
											form.JobEventRecurrenceRule.WeekFrequency === "",
									})}
									onChange={event => onWeekFrequencyInputChange(event)}
									maxLength="2"
								/>
								<span>{`${lang.t("Week")}(s)`}</span>
							</div>
						</div>
					</If>
					<If
						condition={
							form.JobEventRecurrenceRule.RepeatType ===
								RECURRENCE_FREQUENCIES.WEEKDAYS.value ||
							form.JobEventRecurrenceRule.RepeatType ===
								RECURRENCE_FREQUENCIES.WEEKENDS.value ||
							form.JobEventRecurrenceRule.RepeatType ===
								RECURRENCE_FREQUENCIES.WEEKLY.value
						}
					>
						<div className="form-group">
							<div>
								<label>{lang.t("Repeats On")}</label>
							</div>
							<For
								each="item"
								of={DAYS_OF_WEEK}
								index="index"
							>
								<div
									className="DOW-container"
									key={`jobEventsForm_Day_${item.label}`}
								>
									<input
										type="checkbox"
										name={`DOW_${item.shortName}`}
										id={`DOW_${item.shortName}`}
										checked={
											form.JobEventRecurrenceRule.RepeatsOn.indexOf(
												item.shortName,
											) !== -1
										}
										onChange={() => onRecurrenceRepeatsOnChange(item.shortName)}
										disabled={item.shortName === jobStartDOW}
										title={
											item.shortName === jobStartDOW
												? `Job starts on ${item.label}`
												: undefined
										}
									/>
									<label
										htmlFor={`DOW_${item.shortName}`}
										title={
											item.shortName === jobStartDOW
												? `Job starts on ${item.label}`
												: item.label
										}
									>
										{item.label.charAt(0)}
									</label>
								</div>
							</For>
						</div>
					</If>
					<If
						condition={
							form.JobEventRecurrenceRule.RepeatType !==
							RECURRENCE_FREQUENCIES.CUSTOM.value
						}
					>
						<div>
							<label>{lang.t("Ends")}</label>
						</div>
						<div className="radio">
							<label htmlFor="recurrenceTypeNever">
								<input
									type="radio"
									name="JobEventRecurrenceEndType"
									id="recurrenceTypeNever"
									checked={form.JobEventRecurrenceRule.EndType === "NEVER"}
									onChange={() => onRecurrenceEndTypeChange("NEVER")}
								/>
								{lang.t("Never")}
							</label>
						</div>
						<div className="row">
							<div className="col-xs-3 col-sm-2 radio">
								<label htmlFor="recurrenceTypeOccurrence">
									<input
										type="radio"
										name="JobEventRecurrenceEndType"
										id="recurrenceTypeOccurrence"
										checked={
											form.JobEventRecurrenceRule.EndType === "OCCURRENCE"
										}
										onChange={() => onRecurrenceEndTypeChange("OCCURRENCE")}
									/>
									{lang.t("After")}
								</label>
							</div>
							<div
								className={classNames(
									"col-xs-4 col-md-3 form-group recurrence-input-container",
									{
										"invalid-field":
											form.JobEventRecurrenceRule.EndType === "OCCURRENCE" &&
											(form.JobEventRecurrenceRule.EndsAfterOccurrence ===
												"" ||
												parseInt(
													form.JobEventRecurrenceRule.EndsAfterOccurrence,
													10,
												) < 2),
									},
								)}
							>
								<input
									value={form.JobEventRecurrenceRule.EndsAfterOccurrence}
									type="tel"
									name="jobEventRecurrenceEndsAfter"
									id="jobEventRecurrenceEndsAfter"
									onChange={onRecurrenceEndInputChange("EndsAfterOccurrence")}
									className="form-control"
									disabled={form.JobEventRecurrenceRule.EndType !== "OCCURRENCE"}
								/>
							</div>
							<div className="col-xs-5 occurrences-label">
								{lang.t("Occurrences")}
							</div>
						</div>
						<div className="row">
							<div className="col-xs-3 col-sm-2 radio">
								<label htmlFor="recurrenceTypeDate">
									<input
										type="radio"
										name="JobEventRecurrenceEndType"
										id="recurrenceTypeDate"
										checked={form.JobEventRecurrenceRule.EndType === "DATE"}
										onChange={() => onRecurrenceEndTypeChange("DATE")}
									/>
									{lang.t("On")}
								</label>
							</div>
							<div
								className={classNames(
									"col-xs-8 col-sm-4 col-md-3 form-group recurrence-input-container",
									{
										"invalid-field":
											form.JobEventRecurrenceRule.EndType === "DATE" &&
											form.JobEventRecurrenceRule.EndsOn === "",
									},
								)}
							>
								<DatetimeInput
									name="jobEventRecurrenceEndsOn"
									id="jobEventRecurrenceEndsOn"
									minStartDate={form._startDate}
									onJobDateChange={onRecurrenceEndInputChange("EndsOn")}
									startDate={form.JobEventRecurrenceRule.EndsOn}
								/>
							</div>
						</div>
					</If>
				</If>
			</If>
		</div>
	);
};

DateSelector.propTypes = {
	user: PropTypes.object,
	jobEditingProperties: PropTypes.object.isRequired,
	form: PropTypes.object.isRequired,
	updateForm: PropTypes.func.isRequired,
	validateForm: PropTypes.func.isRequired,
};

DateSelector.defaultProps = {
	user: {},
};

export default DateSelector;
